<template>
  <m-dropdown
    v-model:value="visible"
    class="metric-form"
    :title="$t('goal.goalEditor.progressMeasurement')"
    block
    :placement="placement"
    :match-trigger-width="typeof $slots.trigger === 'undefined'"
    :relocate-key="`${goal.progressMeasurement}`"
    :disabled="disabled || readOnly"
  >
    <metric-form-trigger
      :goal="goal"
      :read-only="readOnly"
      :disabled="disabled"
      :m-style="mStyle"
      class="_activator"
    />
    <template #overlay>
      <m-card
        class="_metric-form-overlay"
        no-padding
        border-radius="small"
      >
        <progress-measurement
          class="_progress-measurement"
          :goal="goal"
          @update-progress-measurement="changeProgressMeasurement"
          @update-aggregation-method="changeAggregationMethod"
        />
        <template
          v-if="![goalProgressMeasurement.binary, goalProgressMeasurement.none].includes(goal.progressMeasurement)"
        >
          <m-divider
            none
          />
          <div class="_container">
            <div
              v-if="goal.progressMeasurement === goalProgressMeasurement.alignedItems"
              class="_checkbox"
            >
              <div class="_description">
                {{ $t(`metricForm.alignedItemsDescription.${goal.aggregationMethod}`) }}
              </div>
              <div class="_aligned-items">
                <aligned-item
                  v-for="g in goalChildren"
                  :key="g.uid"
                  class="_checkbox-item"
                  :goal="g"
                  :parent="goal"
                  :weight="weight(g)"
                  :show-weights="showWeights"
                  :total-weight="totalWeight"
                  :is-checked="isChecked(g)"
                  :disabled="readOnly || g.progressMeasurement === goalProgressMeasurement.none"
                  @is-checked-change="updateMeasurementDependencies($event, g)"
                  @weight-change="updateWeight($event, g)"
                />
              </div>
            </div>
            <div
              v-if="goalProgressMeasurement.continuous === goal.progressMeasurement || (goalProgressMeasurement.alignedItems === goal.progressMeasurement && aggregationMethod.absolute === goal.aggregationMethod)"
              :class="['_row', aggregationMethod.absolute === goal.aggregationMethod ? '-padding-below' : '']"
            >
              <div
                v-if="goalProgressMeasurement.alignedItems === goal.progressMeasurement"
                class="_description"
              >
                {{ $t('metricForm.startTargetDescription') }}
              </div>
              <m-alert
                v-if="showMetricFormInfo"
                type="info"
              >
                {{ $t('metricForm.metricFormInfo') }}
              </m-alert>
              <metric-form-range
                :goal="goal"
                :read-only="readOnly"
                @update-metric="updateMetric"
                @update-start-end="updateStartEnd"
              />
              <data-source
                v-if="integrationsAllowed"
                :class="['_data-source']"
                :goal="goal"
                :data-source-id-to-edit="dataSourceIdToEdit"
                :disabled="readOnly || goal.publishedAt === null"
                :disabled-hint="goal.publishedAt === null ? $t('metricForm.dataSourceDisabledHint') : ''"
              />
            </div>
            <div
              v-if="goal.progressMeasurement === goalProgressMeasurement.threshold"
              class="_row"
            >
              <div class="_range">
                <div class="_form-item">
                  <div class="_label">
                    {{ $t('metricForm.threshold') }}
                  </div>
                  <m-input-number
                    :value="threshold"
                    class="_number-input _input-field"
                    :placeholder="$t('general.empty')"
                    :read-only="readOnly"
                    :formatter="formatter"
                    :parser="parser"
                    @input="updateThreshold"
                  />
                </div>
                <div class="_form-item">
                  <div class="_label">
                    {{ $t('metricForm.metric') }}
                  </div>
                  <m-text-field
                    class="_input-field"
                    :value="metric"
                    :max-length="metricMaxLen"
                    :read-only="readOnly"
                    @change="updateMetric"
                  />
                </div>
                <div class="_form-item">
                  <div class="_label">
                    {{ $t('metricForm.coloring') }}
                  </div>
                  <m-select
                    class="_input-field-type"
                    :items="thresholdTargetAreaItems"
                    :value="goal.thresholdTargetArea"
                    :read-only="readOnly"
                    tooltip-placement="right"
                    has-tooltip
                    @change="updateThresholdTargetArea"
                  >
                    <template #item-tooltip="item">
                      <div
                        class="_coloring-item"
                      >
                        <div class="_img">
                          <svg
                            v-if="[goalThresholdTargetArea.below, goalThresholdTargetArea.belowOrEqual].includes(item.item.value)"
                            width="119"
                            height="53"
                            viewBox="0 0 119 53"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.47168"
                              y="0.154541"
                              width="118"
                              height="26"
                              fill="#FADBE2"
                            />
                            <rect
                              x="0.47168"
                              y="26.1545"
                              width="118"
                              height="26"
                              fill="#DEF6F0"
                            />
                            <line
                              x1="0.47168"
                              y1="26.137"
                              x2="118.472"
                              y2="26.137"
                              stroke="#AFAFAC"
                              :stroke-dasharray="goalThresholdTargetArea.below === item.item.value ? '2 2' : undefined"
                            />
                          </svg>
                          <svg
                            v-if="[goalThresholdTargetArea.above, goalThresholdTargetArea.aboveOrEqual].includes(item.item.value)"
                            width="119"
                            height="53"
                            viewBox="0 0 119 53"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.47168"
                              y="0.154541"
                              width="118"
                              height="26"
                              fill="#DEF6F0"
                            />
                            <rect
                              x="0.47168"
                              y="26.1545"
                              width="118"
                              height="26"
                              fill="#FADBE2"
                            />
                            <line
                              x1="0.47168"
                              y1="26.1191"
                              x2="118.472"
                              y2="26.1191"
                              stroke="#AFAFAC"
                              :stroke-dasharray="goalThresholdTargetArea.above === item.item.value ? '2 2' : undefined"
                            />
                          </svg>
                        </div>
                        <div class="_description">
                          <i18n-t keypath="goalThresholdTargetArea.description">
                            <template #placement>
                              <strong>
                                {{ $t(`goalThresholdTargetArea.placement.${camelCase(item.item.value)}`) }}
                              </strong>
                            </template>
                          </i18n-t>
                        </div>
                      </div>
                    </template>
                  </m-select>
                </div>
              </div>
              <div
                v-if="thresholdError !== ''"
                class="_error"
              >
                {{ $t(`metricForm.${thresholdError}`) }}
              </div>
              <div
                v-if="metricError !== ''"
                class="_error"
              >
                {{ $t(`metricForm.${metricError}`, {length: metricMaxLen}) }}
              </div>
              <data-source
                v-if="integrationsAllowed"
                class="_data-source"
                :goal="goal"
                :data-source-id-to-edit="dataSourceIdToEdit"
                :disabled="readOnly"
              />
            </div>
          </div>
          <template v-if="goal.progressMeasurement === goalProgressMeasurement.alignedItems">
            <m-divider none />
            <div class="_container">
              <div class="_row">
                <aligned-item-advanced-options
                  :goal="goal"
                />
              </div>
            </div>
          </template>
        </template>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script>
import AlignedItem from '@/components/goal/AlignedItem.vue';
import AlignedItemAdvancedOptions from '@/components/goal/AlignedItemAdvancedOptions.vue';
import DataSource from '@/components/datasource/DataSource.vue';
import MetricFormRange from '@/components/goal/MetricFormRange.vue';
import MetricFormTrigger from '@/components/goal/MetricFormTrigger.vue';
import ProgressMeasurement from '@/components/goal/ProgressMeasurement.vue';
import useDebounce from 'shared/composables/debounce';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import { aggregationMethod, goalProgressMeasurement, goalThresholdTargetArea, numberFormat } from 'shared/constants.json';
import { camelCase } from 'lodash-es';
import { findInArray } from 'shared/lib/array/array';
import { mStyleProps } from 'shared/lib/m-style-props';
import { numberFormatter, numberParser } from '@/lib/props/number';

export default {
  name: 'MetricForm',
  props: {
    ...mStyleProps,
    goal: {
      type: Object,
      required: true,
    },
    goalChildren: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'onTopLeft',
    },
  },
  setup() {
    const goalsSvc = useGoals();
    const { userLang } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { debounce } = useDebounce();
    return {

      loggedInUserAccount,
      userLang,
      goalsSvc,
      debounce,
    };
  },
  components: { MetricFormTrigger, DataSource, AlignedItem, AlignedItemAdvancedOptions, MetricFormRange, ProgressMeasurement },
  data() {
    return {
      visible: false,
      metric: this.goal.metric,
      metricMaxLen: 16,
      customMetric: '',
      customMetrics: [],
      goalProgressMeasurement,
      aggregationMethod,
      metricError: '',
      thresholdError: '',
      dataSourceIdToEdit: -1,
      goalThresholdTargetArea,
      camelCase,
      start: this.goal.start,
      end: this.goal.end,
      threshold: this.goal.threshold,
    };
  },
  computed: {
    showMetricFormInfo() {
      return this.goal.publishedAt !== null
      && this.goal.progressMeasurement === goalProgressMeasurement.alignedItems
      && this.goalChildren.some((c) => c.publishedAt === null);
    },
    formatter() {
      return numberFormatter(numberFormat.number, this.userLang);
    },
    parser() {
      return numberParser(numberFormat.number, this.userLang);
    },
    thresholdTargetAreaItems() {
      return [
        goalThresholdTargetArea.above,
        goalThresholdTargetArea.aboveOrEqual,
        goalThresholdTargetArea.belowOrEqual,
        goalThresholdTargetArea.below,
      ].map((e) => ({
        text: this.$t(`goalThresholdTargetArea.${camelCase(e)}`),
        value: e,
      }));
    },
    showWeights() {
      if (this.goal.aggregationMethod === aggregationMethod.absolute) {
        return false;
      }

      return this.loggedInUserAccount.accountSettings.usesWeightedGoals;
    },
    integrationsAllowed() {
      if (this.goal.progressMeasurement === goalProgressMeasurement.alignedItems) {
        return false;
      }

      return !this.readOnly && (
        this.loggedInUserAccount.accountSettings.usesJira
        || this.loggedInUserAccount.accountSettings.usesAsana
        || this.loggedInUserAccount.accountSettings.usesSheets
        || this.loggedInUserAccount.accountSettings.usesExcel
        || this.loggedInUserAccount.accountSettings.usesHubspot
        || this.loggedInUserAccount.accountSettings.usesSalesforce
        || this.loggedInUserAccount.accountSettings.usesMSPowerBI
        || this.loggedInUserAccount.accountSettings.usesMSPlanner
        || this.loggedInUserAccount.accountSettings.usesAzureDevOps
      );
    },
    totalWeight() {
      return this.goalChildren.reduce((res, next) => res + this.weight(next), 0);
    },
  },
  methods: {
    weight(goal) {
      const paysOnto = findInArray({ haystack: goal.paysOnto, needle: this.goal.uid });
      if (paysOnto === null) {
        return 0;
      }

      if (typeof paysOnto['paysOnto|weight'] === 'undefined') {
        return 1;
      }

      return paysOnto['paysOnto|weight'];
    },
    isChecked(goal) {
      return goal.paysOnto.map((g) => g.uid).includes(this.goal.uid);
    },
    updateWeight(value, goal) {
      const paysOnto = goal.paysOnto.map((p) => ({
        ...p,
        'paysOnto|weight': value,
      }));

      this.goalsSvc.updateSingle({ uid: goal.uid, paysOnto });
    },
    updateMeasurementDependencies(value, goal) {
      if (value) {
        this.goalsSvc.updateSingle({ uid: goal.uid, paysOnto: [...goal.paysOnto, { uid: this.goal.uid }] });
        return;
      }

      this.goalsSvc.updateSingle({ uid: goal.uid, paysOnto: goal.paysOnto.filter((p) => p.uid !== this.goal.uid) });
    },
    changeProgressMeasurement(value) {
      if (value === goalProgressMeasurement.none) {
        this.goalsSvc.updateSingle({
          uid: this.goal.uid,
          paysOnto: [],
          progressMeasurement: value,
          start: this.goal.start,
          end: this.goal.end,
          threshold: this.goal.threshold,
          thresholdTargetArea: this.goal.thresholdTargetArea,
        });
        return;
      }

      let start = this.goal.start;
      let end = this.goal.end;

      if (value === goalProgressMeasurement.continuous && start === end) {
        start = 0;
        end = 100;
      }

      this.goalsSvc.updateSingle({ uid: this.goal.uid, progressMeasurement: value, start, end, threshold: this.goal.threshold, thresholdTargetArea: this.goal.thresholdTargetArea });
    },
    changeAggregationMethod(value) {
      this.goalsSvc.updateSingle({ uid: this.goal.uid, aggregationMethod: value });
    },
    updateStartEnd(start, end) {
      this.goalsSvc.updateSingle({ uid: this.goal.uid, start, end });
    },
    updateThreshold(value) {
      this.threshold = value;
      if (value === '' || value === null || Number.isNaN(value)) {
        this.thresholdError = 'notEmpty';
        return;
      }

      this.thresholdError = '';
      const update = () => {
        this.goalsSvc.updateSingle({ uid: this.goal.uid, threshold: value });
      };
      this.debounce(update, 500, 'updateThreshold');
    },
    updateThresholdTargetArea(value) {
      this.goalsSvc.updateSingle({ uid: this.goal.uid, thresholdTargetArea: value });
    },
    updateMetric(value) {
      this.metric = value;
      if (value === this.goal.metric) {
        return;
      }

      if (value.length === this.metricMaxLen) {
        this.metricError = 'metricMaxLen';
        return;
      }

      this.metricError = '';
      this.goalsSvc.updateSingle({ uid: this.goal.uid, metric: value });
    },
  },
  watch: {
    visible() {
      this.metricError = '';
      this.thresholdError = '';
    },
  },
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._metric-form-overlay {
    min-width: 40rem;
    max-height: calc(var(--viewport-height-100) - 13rem);
    overflow: auto;

    ._dropdown {
      width: 100%;
    }

    @media (max-width: $screen-size-md) {
      min-width: unset;
    }

    ._container {
      padding: .8rem 1.6rem 1.2rem;
      display: flex;
      flex-direction: column;
      align-items: center;

      ._row {
        display: flex;
        flex-direction: column;
        width: 100%;

        ._range {
          display: flex;
          width: 100%;
        }

        &.-padding-below {
          padding-bottom: 1.6rem;
        }
      }

      ._input-field {
        width: 10rem;
        margin-right: .8rem;
      }
    }

    ._description {
      font-size: $font-size-2;
      font-weight: 500;
      margin-bottom: .8rem;
      margin-top: .8rem;
      color: $font-color-secondary;
    }

    ._progress-measurement {
      padding: .8rem .9rem;
    }

    ._form-item {
      margin-bottom: .8rem;

      &.-has-margin {
        margin-bottom: 2rem;
      }

      ._label {
        margin-bottom: .4rem;
        font-size: $font-size-2;
        color: $font-color-tertiary;

        &.-progress {
          padding: 0 1.2rem;
          margin-top: 1.2rem;
        }
      }
    }

    ._error {
      margin-top: .8rem;
      font-size: $font-size-2;
      color: $error-color;
    }

    ._data-source {
      margin-top: 2rem;
    }

    ._checkbox {
      width: 100%;

      ._checkbox-item {
        margin-bottom: .4rem;
      }
    }
  }

  ._coloring-item {
    width: 100%;

    ._img {
      display: flex;
      padding: .8rem;
      background-color: white;
      border-radius: $border-radius-sm;
    }

    ._description {
      max-width: 11.4rem;
      margin-top: .4rem;
      font-size: $font-size-2;
    }
  }
</style>
